/**
 * THE TILL POSTER. The thing a shopkeeper actually prints and tapes to the counter.
 *
 * A till only works if a customer can act on it without being told anything, so the poster carries
 * every fact the payment needs and nothing it does not:
 *
 *   the mark and VASP CRYPTO TILL: <ASSET>, so it is obvious what this is and what it takes
 *   the QR, big enough to scan across a counter
 *   the till number in large monospace, because a phone camera fails and a person reads instead
 *   the asset and the network, twice, in words and in the handle
 *   the warning that another network loses the money
 *
 * ## Two decisions worth defending
 *
 * THE ASSET IS IN THE TITLE, not just in the details. A poster is read at a glance from a metre away,
 * and the glance has to answer "can I pay with what I have". A customer holding BTC must not queue up
 * at a USDT till and find out at the till.
 *
 * THE SIMULATION WARNING IS PRINTED, in a box, not tucked into small print. This poster leaves the
 * app: it gets taped to a wall and photographed. Custody is a simulator (CLAUDE.md gap 10), so an
 * address on it cannot receive real funds, and a printed artefact that omitted that would be the one
 * piece of this platform most likely to cost somebody real money. It is rendered from
 * `address_is_simulated`, served with the record, so it disappears the day custody is real.
 */

import type { Till } from './api';
import { BRAND_PINE } from './brand-colors';
import { PdfPage, mm, renderPdf, textWidth, type Bitmap } from './pdf';
import { tillSegments } from './till';

/** The pine tile, as PDF ink. Parsed from the one place the literal lives. */
const PINE = {
  r: parseInt(BRAND_PINE.slice(1, 3), 16) / 255,
  g: parseInt(BRAND_PINE.slice(3, 5), 16) / 255,
  b: parseInt(BRAND_PINE.slice(5, 7), 16) / 255,
};
const BLACK = { r: 0.09, g: 0.11, b: 0.1 };
const GREY = { r: 0.42, g: 0.45, b: 0.44 };
const WARN_INK = { r: 0.55, g: 0.35, b: 0.02 };
const WARN_BG = { r: 0.99, g: 0.96, b: 0.89 };
const WHITE = { r: 1, g: 1, b: 1 };

/**
 * Read the rendered QR canvas back as a 1-bit bitmap.
 *
 * Thresholding is safe here rather than lossy: the canvas holds a QR drawn in two flat colours, so
 * every pixel is already at one end or the other and the midpoint separates them exactly.
 */
export function canvasToBitmap(canvas: HTMLCanvasElement): Bitmap | null {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  return {
    width,
    height,
    isBlack: (x, y) => {
      const i = (y * width + x) * 4;
      // Luminance, weighted as the eye sees it. Anything below mid grey is a module.
      return data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114 < 128;
    },
  };
}

/** What the QR encodes. The app's own payment URI, carrying the till and nothing else. */
export function tillPaymentUri(till: Pick<Till, 'till_number'>): string {
  return `vasp:pay?till=${till.till_number}`;
}

/** `lipa-till-usdttrc204k7q2m`. Used for both the PDF and the PNG, so a phone's files sort. */
export function tillFileStem(till: Pick<Till, 'till_number'>): string {
  return `lipa-till-${till.till_number.replace(/^LP/, '')}`.toLowerCase();
}

/**
 * The poster, as a PDF blob. A5 portrait: big enough to read across a counter, small enough that a
 * shop can print four to a sheet without a designer.
 */
export function buildTillPoster(till: Till, qr: Bitmap): Blob {
  const width = mm(148);
  const height = mm(210);
  const page = new PdfPage(width, height);
  const centre = width / 2;
  const parts = tillSegments(till);

  // Paper. Explicit, because a viewer's default is not guaranteed to be white.
  page.fill(WHITE).rect(0, 0, width, height);

  // ---- THE MARK. The pine tile with its white L, drawn rather than embedded: it is a rounded
  // square and a letter, and a raster logo would print soft at any size a poster is read from.
  const tile = mm(16);
  const tileY = height - mm(18) - tile;
  page.fill(PINE).roundedRect(centre - tile / 2, tileY, tile, tile, mm(3.5));
  page.fill(WHITE).textCentered('L', centre, tileY + tile / 2 - mm(3.6), mm(9.5), 'Helvetica-Bold');

  // ---- THE TITLE. The asset is appended, so the glance answers "what does this take".
  page
    .fill(PINE)
    .textCentered(
      'VASP CRYPTO TILL: ' + till.asset,
      centre,
      tileY - mm(9),
      mm(5),
      'Helvetica-Bold',
    );
  page
    .fill(GREY)
    .textCentered(
      `${till.asset} on ${till.network} (${till.network_tag})`,
      centre,
      tileY - mm(15),
      mm(3.6),
      'Helvetica',
    );

  // ---- THE QR, on a white square with a hairline frame so it survives being photographed.
  const qrSize = mm(74);
  const qrY = tileY - mm(23) - qrSize;
  page
    .fill({ r: 0.85, g: 0.87, b: 0.86 })
    .rect(centre - qrSize / 2 - 2, qrY - 2, qrSize + 4, qrSize + 4);
  page.fill(WHITE).rect(centre - qrSize / 2, qrY, qrSize, qrSize);
  page.image(qr, centre - qrSize / 2, qrY, qrSize, qrSize);

  // ---- THE HANDLE. Monospaced and large: this is the fallback when the camera will not focus, and
  // it is also what somebody dictates over the phone. Split across two lines when it will not fit,
  // because shrinking it to fit is how a poster becomes unreadable from two steps back.
  const handleSize = mm(6.2);
  const handleFits = textWidth(till.till_number, handleSize, 'Courier-Bold') < width - mm(20);
  page.fill(BLACK);
  if (handleFits || !parts) {
    page.textCentered(till.till_number, centre, qrY - mm(12), handleSize, 'Courier-Bold');
  } else {
    page.textCentered(
      `LP ${parts.asset} ${parts.networkTag}`,
      centre,
      qrY - mm(11),
      mm(5),
      'Courier-Bold',
    );
    page.textCentered(parts.code, centre, qrY - mm(18), mm(7.5), 'Courier-Bold');
  }

  const detailTop = qrY - (handleFits || !parts ? mm(22) : mm(28));
  page
    .fill(GREY)
    .textCentered(
      'Scan with VASP, or type this Till in Send Money.',
      centre,
      detailTop,
      mm(3.2),
    );

  // ---- THE RULE THAT COSTS MONEY. Boxed, not footnoted.
  const boxHeight = mm(18);
  const boxY = detailTop - mm(8) - boxHeight;
  page.fill(WARN_BG).rect(mm(12), boxY, width - mm(24), boxHeight);
  page
    .fill(WARN_INK)
    .textCentered(
      `Accepts ${till.asset} on ${till.network} (${till.network_tag}) only.`,
      centre,
      boxY + boxHeight - mm(6.5),
      mm(3.8),
      'Helvetica-Bold',
    );
  page
    .fill(WARN_INK)
    .textCentered(
      'Anything sent on another network will not arrive and cannot be recovered.',
      centre,
      boxY + boxHeight - mm(12),
      mm(3.1),
    );

  // ---- THE SIMULATION DISCLOSURE. On the printed artefact, because the printed artefact is the one
  // that outlives the sandbox.
  let footerY = boxY - mm(10);
  if (till.address_is_simulated) {
    page
      .fill(WARN_INK)
      .textCentered(
        'SANDBOX: this Till is simulated and cannot receive funds on a live network.',
        centre,
        footerY,
        mm(3.1),
        'Helvetica-Bold',
      );
    footerY -= mm(6);
  }

  page
    .fill(GREY)
    .textCentered(
      'VASP operates in the Capital Markets Authority regulatory sandbox.',
      centre,
      footerY,
      mm(2.8),
    );

  return renderPdf(page, `VASP ${till.asset} Till ${till.till_number}`);
}
